import { Check, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TypoCaption } from "@ui/text/Typo";
import { ITheme } from "@ui/theme/GlobalTheme";

export interface FilterMultiSelectOption {
	value: string;
	name: string;
	detail?: string;
}

/** Matches DateRangePicker's field so the overview filters read as one row. */
const Field = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
	font-family: "Euclid Circular B", sans-serif;
	.control { position: relative; min-width: 0; }
	.trigger {
		display: flex; align-items: center; gap: 8px; width: 100%; height: 36px; box-sizing: border-box; padding: 0 12px;
		border: 1px solid ${({ theme }) => (theme as ITheme).colors.inputBorderPrimary}; border-radius: 4px; background: ${({ theme }) => (theme as ITheme).colors.panelBackgroundPrimary}; color: ${({ theme }) => (theme as ITheme).colors.textPrimary}; font: inherit; font-size: 13px; line-height: 1.25; text-align: left; cursor: pointer;
		transition: border-color .2s;
	}
	.trigger:hover:not(:disabled) { border-color: #35539f; }
	.trigger:focus-visible, .trigger[aria-expanded="true"] { outline: none; border-color: #35539f; }
	.trigger:disabled { background: #f9fafb; color: #98a2b3; cursor: default; }
	.trigger > svg { flex-shrink: 0; color: #667085; }
	.trigger.clearable { padding-right: 56px; }
	.trigger.clearable > .chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); }
	.value { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.value.placeholder { color: #667085; }
	.count { flex-shrink: 0; padding: 1px 7px; border: 1px solid #d0d5dd; border-radius: 6px; background: #f9fafb; color: #344054; font-size: 11px; font-weight: 500; }
	.clear {
		position: absolute; right: 30px; top: 50%; transform: translateY(-50%); display: grid; place-items: center; width: 20px; height: 20px; padding: 0;
		border: 0; border-radius: 5px; background: transparent; color: #98a2b3; cursor: pointer;
	}
	.clear:hover { background: #f2f4f7; color: #344054; }
	.clear:focus-visible { outline: 2px solid #141413; outline-offset: 1px; }
`;

const Popover = styled.div`
	position: fixed;
	z-index: 1100;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	max-height: min(420px, calc(100dvh - 16px));
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 16px 40px rgba(16, 24, 40, .16), 0 2px 6px rgba(16, 24, 40, .06);
	color: #344054;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	overflow: hidden;
	button, input { font: inherit; }
	.search { position: relative; padding: 10px 10px 8px; border-bottom: 1px solid #eaecf0; }
	.search svg { position: absolute; left: 21px; top: 50%; transform: translateY(-50%); margin-top: 1px; color: #98a2b3; pointer-events: none; }
	.search input { width: 100%; height: 34px; box-sizing: border-box; padding: 0 10px 0 32px; border: 1px solid #d0d5dd; border-radius: 8px; color: #182230; font-size: 13px; }
	.search input:focus { outline: none; border-color: #141413; box-shadow: 0 0 0 3px rgba(20, 20, 19, .1); }
	.list { flex: 1; min-height: 0; overflow-y: auto; padding: 6px; }
	.option { display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 10px; border: 0; border-radius: 8px; background: transparent; color: #344054; text-align: left; cursor: pointer; }
	.option:hover, .option:focus-visible { outline: none; background: #f2f4f7; }
	.option.all { font-weight: 500; }
	.box { display: grid; place-items: center; flex-shrink: 0; width: 16px; height: 16px; box-sizing: border-box; border: 1px solid #d0d5dd; border-radius: 4px; background: #fff; color: #fff; }
	.option[aria-selected="true"] .box { border-color: #35539f; background: #35539f; }
	.name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.detail { flex-shrink: 0; color: #98a2b3; font-size: 12px; font-variant-numeric: tabular-nums; }
	.divider { height: 1px; margin: 4px 4px; background: #eaecf0; }
	.state { padding: 14px 10px; color: #667085; text-align: center; }
	.footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 8px 12px; border-top: 1px solid #eaecf0; color: #667085; font-size: 12px; }
	.footer button { padding: 4px 10px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #344054; cursor: pointer; }
	.footer button:hover { background: #f2f4f7; }
`;

interface FilterMultiSelectProps {
	label: string;
	icon?: ReactNode;
	options: FilterMultiSelectOption[];
	selected: string[];
	onChange: (values: string[]) => void;
	/** Text shown when nothing is selected; selecting nothing means "no filter". */
	allLabel: string;
	searchable?: boolean;
	busy?: boolean;
	error?: boolean;
	disabled?: boolean;
	style?: CSSProperties;
}

export const FilterMultiSelect = ({ label, icon, options, selected, onChange, allLabel, searchable = false, busy = false, error = false, disabled = false, style }: FilterMultiSelectProps) => {
	const { t } = useTranslation();
	const labelId = useId();
	const listId = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [position, setPosition] = useState<CSSProperties>({ visibility: "hidden" });

	const selectedSet = new Set(selected);
	const selectedOptions = options.filter(option => selectedSet.has(option.value));
	const valueText = selectedOptions.length === 0
		? allLabel
		: selectedOptions.length === 1 ? selectedOptions[0].name : selectedOptions.map(option => option.name).join(", ");

	const close = (focusTrigger = true) => {
		setOpen(false);
		if (focusTrigger) triggerRef.current?.focus();
	};

	useLayoutEffect(() => {
		if (!open) return;
		const place = () => {
			const rect = triggerRef.current?.getBoundingClientRect();
			if (!rect) return;
			const width = Math.max(rect.width, 260);
			const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
			const below = window.innerHeight - rect.bottom - 8;
			const height = popoverRef.current?.offsetHeight ?? 0;
			const top = below < Math.min(height, 240) && rect.top > below ? Math.max(8, rect.top - 4 - height) : rect.bottom + 4;
			setPosition({ top, left, width });
		};
		place();
		window.addEventListener("resize", place);
		window.addEventListener("scroll", place, true);
		return () => {
			window.removeEventListener("resize", place);
			window.removeEventListener("scroll", place, true);
		};
	}, [open, options.length]);

	useEffect(() => {
		if (!open) return;
		if (searchable) searchRef.current?.focus();
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (!popoverRef.current?.contains(target) && !triggerRef.current?.contains(target)) close(false);
		};
		document.addEventListener("pointerdown", onPointerDown, true);
		return () => document.removeEventListener("pointerdown", onPointerDown, true);
	}, [open, searchable]);

	const toggle = (value: string) => onChange(selectedSet.has(value) ? selected.filter(item => item !== value) : [...selected, value]);

	const term = search.trim().toLocaleLowerCase();
	const matches = term ? options.filter(option => option.name.toLocaleLowerCase().includes(term) || option.value.includes(term)) : options;
	const clearable = selected.length > 0 && !disabled;

	return <Field style={style}>
		<span id={labelId}><TypoCaption block>{label}:</TypoCaption></span>
		<div className="control">
			<button
				ref={triggerRef}
				type="button"
				className={`trigger${clearable ? " clearable" : ""}`}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={open ? listId : undefined}
				aria-labelledby={labelId}
				disabled={disabled}
				title={selectedOptions.length > 1 ? valueText : undefined}
				onKeyDown={event => { if (event.key === "Escape" && open) { event.stopPropagation(); close(); } }}
				onClick={() => { if (open) close(); else { setSearch(""); setPosition({ visibility: "hidden" }); setOpen(true); } }}>
				{icon}
				<span className={`value${selectedOptions.length ? "" : " placeholder"}`}>{valueText}</span>
				{selectedOptions.length > 1 && <span className="count">{selectedOptions.length}</span>}
				<ChevronDown className="chevron" size={16} aria-hidden="true" />
			</button>
			{clearable && <button type="button" className="clear" aria-label={t("dateRange.clear")} onClick={() => onChange([])}>
				<X size={14} aria-hidden="true" />
			</button>}
		</div>
		{open && createPortal(<Popover ref={popoverRef} style={position} onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); close(); } }}>
			{searchable && <div className="search">
				<Search size={15} aria-hidden="true" />
				<input ref={searchRef} type="search" value={search} placeholder={t("stats.filters.search")} aria-label={t("stats.filters.search")} onChange={event => setSearch(event.target.value)} />
			</div>}
			<div className="list" id={listId} role="listbox" aria-multiselectable="true" aria-labelledby={labelId}>
				{!term && <>
					<button type="button" role="option" className="option all" aria-selected={selected.length === 0} onClick={() => onChange([])}>
						<span className="box">{selected.length === 0 && <Check size={12} strokeWidth={3} aria-hidden="true" />}</span>
						<span className="name">{allLabel}</span>
					</button>
					{options.length > 0 && <div className="divider" role="presentation" />}
				</>}
				{busy && !options.length ? <div className="state" role="status">{t("common.loading")}</div>
					: error && !options.length ? <div className="state" role="alert">{t("stats.filters.loadError")}</div>
					: !matches.length ? <div className="state">{t("stats.filters.noMatches")}</div>
					: matches.map(option => {
						const active = selectedSet.has(option.value);
						return <button type="button" role="option" className="option" aria-selected={active} key={option.value} onClick={() => toggle(option.value)}>
							<span className="box">{active && <Check size={12} strokeWidth={3} aria-hidden="true" />}</span>
							<span className="name" title={option.name}>{option.name}</span>
							{option.detail && <span className="detail">{option.detail}</span>}
						</button>;
					})}
			</div>
			{selected.length > 0 && <div className="footer">
				<span>{t("stats.filters.selected", { count: selected.length })}</span>
				<button type="button" onClick={() => onChange([])}>{t("dateRange.clear")}</button>
			</div>}
		</Popover>, document.body)}
	</Field>;
};
